import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSignedPhoto } from '../hooks/useSignedPhoto'

interface ProfileAvatarProps {
  name: string
  photoUrl?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number
  className?: string
  style?: React.CSSProperties
  showPersonIcon?: boolean // New prop for placeholder mode
}

const sizeMap = {
  xs: 24,
  sm: 32,
  md: 48,
  lg: 64,
  xl: 80
}

const getInitials = (name: string): string => {
  if (!name || !name.trim()) return '?'
  
  const words = name.trim().split(/\s+/)
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase()
  }
  
  // Take first letter of first and last name
  return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase()
}

const generateColorFromName = (name: string): { backgroundColor: string; color: string } => {
  // Generate a consistent color based on the name
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  
  // Generate a pleasant color palette
  const colors = [
    { bg: '#EF4444', text: '#FFFFFF' }, // red
    { bg: '#F97316', text: '#FFFFFF' }, // orange
    { bg: '#EAB308', text: '#FFFFFF' }, // yellow
    { bg: '#22C55E', text: '#FFFFFF' }, // green
    { bg: '#06B6D4', text: '#FFFFFF' }, // cyan
    { bg: '#3B82F6', text: '#FFFFFF' }, // blue
    { bg: '#8B5CF6', text: '#FFFFFF' }, // violet
    { bg: '#EC4899', text: '#FFFFFF' }, // pink
    { bg: '#6B7280', text: '#FFFFFF' }, // gray
    { bg: '#DC2626', text: '#FFFFFF' }, // dark red
  ]
  
  const colorIndex = Math.abs(hash) % colors.length
  return {
    backgroundColor: colors[colorIndex].bg,
    color: colors[colorIndex].text
  }
}

const PersonIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg 
    width={size * 0.6} 
    height={size * 0.6} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
)

export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({ 
  name, 
  photoUrl, 
  size = 'md',
  className = '',
  style = {},
  showPersonIcon = false
}) => {
  const { t } = useTranslation()
  const actualSize = typeof size === 'number' ? size : sizeMap[size]
  
  // Handle different types of photo URLs
  const isLocalPreview = photoUrl?.startsWith('blob:') || photoUrl?.startsWith('data:')
  const signedPhoto = useSignedPhoto(isLocalPreview ? undefined : photoUrl)
  
  // Use local preview directly, or signed photo for GCS URLs
  const displayUrl = isLocalPreview ? photoUrl : signedPhoto
  
  const initials = getInitials(name)
  const colors = generateColorFromName(name)

  const baseStyle: React.CSSProperties = {
    width: `${actualSize}px`,
    height: `${actualSize}px`,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: `${Math.max(actualSize * 0.4, 12)}px`,
    fontWeight: 'bold',
    border: '2px solid #E5E7EB',
    flexShrink: 0,
    ...style
  }

  if (displayUrl) {
    return (
      <div
        className={className}
        style={{
          ...baseStyle,
          backgroundImage: `url(${displayUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#F3F4F6'
        }}
        title={name}
        aria-label={t('profile_avatar.picture_aria', { name })}
      />
    )
  }

  // Show person icon in placeholder mode (for Add/Edit profile pages)
  if (showPersonIcon) {
    return (
      <div
        className={className}
        style={{
          ...baseStyle,
          backgroundColor: '#F3F4F6',
          color: '#9CA3AF'
        }}
        title={name}
        aria-label={t('profile_avatar.placeholder_aria', { name })}
      >
        <PersonIcon size={actualSize} />
      </div>
    )
  }

  return (
    <div
      className={className}
      style={{
        ...baseStyle,
        backgroundColor: colors.backgroundColor,
        color: colors.color
      }}
      title={name}
      aria-label={t('profile_avatar.initials_aria', { name })}
    >
      {initials}
    </div>
  )
} 